import { hashKey } from "@tanstack/react-query";
import { queryClient as defaultQueryClient } from "./QueryUtils";

/**
 * Subscribes to updates for a single TanStack Query cache entry. Data already
 * in the cache at subscribe time is NOT replayed -- callers that need the
 * current value read it synchronously before subscribing.
 * @param {unknown[] | string} queryKey TanStack Query key to watch.
 * @param {(data: unknown) => void} callback Called when cached data changes.
 * @param {import("@tanstack/react-query").QueryClient} [client] Query client.
 * @returns {() => void} Unsubscribe function.
 */
export function subscribeToQuery(queryKey, callback, client = defaultQueryClient) {
  const targetHash = hashKey(queryKey);
  const queryCache = client.getQueryCache();

  // Dedupe by DATA REFERENCE, not dataUpdatedAt: two writes inside the same
  // millisecond share a timestamp and would coalesce into one delivery.
  let lastData = queryCache.get(targetHash)?.state.data;

  return queryCache.subscribe((event) => {
    const query = event.query;
    if (!query || query.queryHash !== targetHash) return;

    const { data } = query.state;
    if (data === undefined || data === lastData) return;

    lastData = data;
    callback(data);
  });
}
